import { orderBy } from 'lodash';
import type { Actor } from '@avatar/types';
import {
  ActorAccessLevel,
  ActorVariant,
} from '../../../../server/graphql/__generated.types';
import { LocaleEnum_V2 } from '../../../../server/graphql/__generated-graphcms.types';

/**
 * Order matters for these util functions, do not change.
 * Used for favorite/team voice deduplication.
 */
const LEGACY_IDS = [7, 12, 17];
const V2_IDS = [30, 31, 32];

export function isDuplicateVariant(id?: number): boolean {
  if (!id) return false;
  return [...LEGACY_IDS, ...V2_IDS].includes(id);
}

export function filterAvatarsByLocale(
  avatars: Actor[],
  locale: LocaleEnum_V2
): Actor[] {
  const reformattedLocale = locale.replace('_', '-');
  const localeFilteredAvatars = avatars.map(actor => ({
    ...actor,
    variants: actor.variants.filter(variant => {
      return variant.locale === reformattedLocale;
    }),
  }));

  return localeFilteredAvatars.filter(actor => actor.variants.length > 0);
}

/**
 * Get list of ids
 */
export const getDuplicateOverrideVariants = (id?: number): Array<number> => {
  const variants: Array<number> = [];

  V2_IDS.forEach((v, i) => {
    if (id === v) {
      variants.push(id);
      variants.push(LEGACY_IDS[i]);
    }
  });
  LEGACY_IDS.forEach((v, i) => {
    if (id === v) {
      variants.push(id);
      variants.push(V2_IDS[i]);
    }
  });

  return variants;
};

export const isV2DuplicateVariant = (id?: number) => {
  if (!id) return false;
  return V2_IDS.includes(id);
};

export const mapV2ToCurrentDuplicateVariant = (
  id?: number
): number | undefined => {
  if (!id) return undefined;
  V2_IDS.forEach((v, i) => {
    if (id === v) return LEGACY_IDS[i];
  });
};

export const getDuplicateVoiceStyleVariantIDs = (actor: Actor) => {
  const variantMap: { [key: string]: number[] } = {};
  const duplicateSet: Set<number> = new Set();

  actor.variants?.forEach(variant => {
    const { label } = variant.variantType;
    if (!variantMap[label]) {
      variantMap[label] = [];
    }
    variantMap[label].push(variant.id);
  });
  Object.values(variantMap).forEach(ids => {
    if (ids.length > 1) {
      ids.forEach(id => duplicateSet.add(id));
    }
  });

  return duplicateSet;
};

export const checkIsFavorite = (actor: Actor, variantId?: number): boolean => {
  if (variantId) {
    // Checks if either duplicate variant is favorited
    const duplicateIds = getDuplicateOverrideVariants(variantId);
    if (duplicateIds.length > 0) {
      return Boolean(
        actor.variants
          .filter(v => duplicateIds.includes(v.id))
          .find(v => v?.userSettings?.favoritedAt)
      );
    }
    const foundVariant = actor.variants?.find(v => v.id === variantId);
    return !!foundVariant?.userSettings?.favoritedAt;
  }
  return actor.variants.some(v => v.userSettings?.favoritedAt);
};

/**
 * Splits a list of actors into favorites and team voices (each actor with
 * variants array containing only the favorited/team variants).
 */
export function getFavoritesAndTeamVoices(actors: Actor[]) {
  const favorites: Actor[] = [];
  const teamVoices: Actor[] = [];

  actors.forEach(actor => {
    if (actor.accessLevel !== ActorAccessLevel.Locked) {
      const variants: ActorVariant[] = [];
      const teamVariants: ActorVariant[] = [];

      actor.variants.forEach(variant => {
        if (variant.userSettings?.favoritedAt) {
          if (!isDuplicateVariant(variant.id)) {
            favorites.push({ ...actor, variants: [variant] });
          } else {
            variants.push(variant);
          }
        }
        if (variant.teamSettings?.favoritedAt) {
          if (!isDuplicateVariant(variant.id)) {
            teamVoices.push({ ...actor, variants: [variant] });
          } else {
            teamVariants.push(variant);
          }
        }
      });

      if (isDuplicateVariant(actor.id)) {
        if (variants.length > 0) {
          favorites.push({ ...actor, variants });
        }
        if (teamVariants.length > 0) {
          teamVoices.push({ ...actor, variants: teamVariants });
        }
      }
    }
  });

  const sortByNameAndVariantLabel = (unsortedActors: Actor[]) => {
    const accessVariantLabel = (actor: Actor) =>
      actor.variants[0]?.variantType.label ?? '';
    return orderBy(
      orderBy(unsortedActors, accessVariantLabel, ['asc']),
      ['name'],
      ['asc']
    );
  };

  return {
    favorites: sortByNameAndVariantLabel(favorites),
    teamVoices: sortByNameAndVariantLabel(teamVoices),
  };
}
